import {
  BookOpenIcon,
  CameraIcon,
  MicIcon,
  SearchIcon,
  UsersIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type BadgeKey = 'ambassador' | 'archivist' | 'storyteller' | 'namer' | 'listener'

export interface BadgeDefinition {
  key: BadgeKey
  label: string
  icon: LucideIcon
  /** How it is earned, stated plainly. Never a target to chase. */
  criteria: string
  /** Shown on the member's own profile once earned. */
  earnedNote: string
}

/**
 * Recognition for what someone has already done for the community — never a
 * progress bar, never a comparison, never a count of what is still missing.
 */
export const BADGES: Record<BadgeKey, BadgeDefinition> = {
  ambassador: {
    key: 'ambassador',
    label: 'Ambassador',
    icon: UsersIcon,
    criteria: 'Invited two or more people who joined',
    earnedNote: 'Two people are here because you thought of them.',
  },
  archivist: {
    key: 'archivist',
    label: 'Archivist',
    icon: CameraIcon,
    criteria: 'Added two or more photographs or artifacts',
    earnedNote: 'You have put things into the archive that nobody else had.',
  },
  storyteller: {
    key: 'storyteller',
    label: 'Storyteller',
    icon: BookOpenIcon,
    criteria: 'Shared a memory that is now in the archive',
    earnedNote: 'Your account of a year is part of the record.',
  },
  namer: {
    key: 'namer',
    label: 'Namer',
    icon: SearchIcon,
    criteria: 'Identified people in five or more photographs',
    earnedNote: 'Faces that had no name have one because of you.',
  },
  listener: {
    key: 'listener',
    label: 'Listener',
    icon: MicIcon,
    criteria: 'Helped transcribe or identify voices in a recording',
    earnedNote: 'You gave a recording its words back.',
  },
}

/** Who has earned what. Keyed by full member name. */
export const MEMBER_BADGES: Record<string, BadgeKey[]> = {
  'Ray Mendel': ['archivist', 'storyteller'],
  'Theresa Molina': ['storyteller'],
  'Dev Rao': ['storyteller', 'archivist', 'namer'],
  'Marion Alvarez': ['archivist', 'ambassador'],
  'Andre Price': ['ambassador', 'namer'],
  'Sean Duffy': ['storyteller', 'listener'],
  'Nadia Kaur': ['archivist'],
}

/**
 * Names reach this from several places — some full ("Ray Mendel"), some already
 * public ("Ray M."). Match on the first name plus the last initial so badges
 * appear consistently wherever a person is listed.
 */
function keyOf(name: string): string {
  const [first = '', last = ''] = name.trim().split(/\s+/)
  return `${first.toLowerCase()}|${last.charAt(0).toLowerCase()}`
}

const BY_KEY: Record<string, BadgeKey[]> = Object.entries(MEMBER_BADGES).reduce<
  Record<string, BadgeKey[]>
>((lookup, [name, keys]) => {
  lookup[keyOf(name)] = keys
  return lookup
}, {})

export function badgesFor(name: string): BadgeDefinition[] {
  return (BY_KEY[keyOf(name)] ?? []).map((key) => BADGES[key])
}

/** The one badge shown as an icon beside a name in lists. */
export function primaryBadge(name: string): BadgeDefinition | null {
  return badgesFor(name)[0] ?? null
}
